import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";

const VendorPaymentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    date: new Date(),
  });
  const [error, setError] = useState({});
  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankBalance, setBankBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  useEffect(() => {
    Promise.all([
      Http.get("/vendor"),
      Http.get("/accounts/banks"),
      Http.get(`/accounts/vendor-payments/${id}`)
    ])
      .then(([vendorsRes, banksRes, paymentRes]) => {
        setVendors(vendorsRes.data);
        setBanks(banksRes.data);
        
        const payment = paymentRes.data.data;
        const paymentData = {
          vendor_id: payment.vendor_id,
          bank_id: payment.bank_id,
          amount: payment.amount,
          date: new Date(payment.date),
          description: payment.description || "",
          reference_no: payment.payment_number || "",
          payment_method: payment.payment_method || ""
        };
        setInputs(paymentData);
        
        // Fetch bank balance for the current bank
        if (payment.bank_id) {
          fetchBankBalance(payment.bank_id);
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        FormValidation.error("Failed to fetch data");
        navigate("/vendor-payments");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const fetchBankBalance = (bankId) => {
    if (!bankId) {
      setBankBalance(null);
      setBalanceError("");
      return;
    }

    setLoadingBalance(true);
    setBalanceError("");

    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalance(res.data.data);
        } else {
          setBalanceError("Failed to fetch bank balance");
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceError("Error fetching bank balance");
        setBankBalance(null);
      })
      .finally(() => {
        setLoadingBalance(false);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value);
    }
  };

  const handleDateChange = (e) => {
    setInputs((values) => ({ ...values, date: e.value }));
  };

  const formSubmit = () => {
    // Create a copy of inputs and adjust the date
    const submissionData = {
      ...inputs,
      date: new Date(inputs.date.getTime() - inputs.date.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0],
    };

    Http.put(`/accounts/vendor-payments/${id}`, submissionData)
      .then((res) => {
        navigate("/vendor-payments", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      {loading ? <LoaderComponent /> : null}
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Vendor Payment</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Payment Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>
            <div className="row">
              <div className="col-lg">
                <label>Payment Date <span className="text-danger">*</span></label>
                <Calendar
                  value={inputs.date}
                  onChange={handleDateChange}
                  dateFormat="dd/mm/yy"
                  className="w-100"
                />
              </div>
              <div className="col-lg">
                <label>Reference No</label>
                <input
                  type="text"
                  className="form-control"
                  name="reference_no"
                  value={inputs.reference_no || ""}
                  onChange={handleChange}
                  placeholder="Enter Reference Number"
                />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-lg">
                <label>Vendor <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  name="vendor_id"
                  value={inputs.vendor_id || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg">
                <label>Bank <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  name="bank_id"
                  value={inputs.bank_id || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Bank</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bank Balance Display */}
            {inputs.bank_id && (
              <div className="row mt-2">
                <div className="col-lg-6">
                  <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                    <strong>Bank Balance:</strong>
                    {loadingBalance ? (
                      <div className="d-flex align-items-center mt-2">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <span>Loading balance...</span>
                      </div>
                    ) : balanceError ? (
                      <div className="text-danger mt-2">
                        <i className="fa fa-exclamation-triangle me-1"></i>
                        {balanceError}
                      </div>
                    ) : bankBalance ? (
                      <div className="mt-2">
                        <div className="row">
                          <div className="col-md-4">
                            <small className="text-muted">Bank Name:</small>
                            <div className="font-weight-bold">{bankBalance.name}</div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">Account Number:</small>
                            <div className="font-weight-bold">{bankBalance.account_number}</div>
                          </div>
                          <div className="col-md-4">
                            <small className="text-muted">Available Balance:</small>
                            <div className="font-weight-bold text-info">
                              ৳ {parseFloat(bankBalance.total_balance).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className="row mt-3">
              <div className="col-lg">
                <label>Amount <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  name="amount"
                  value={inputs.amount || ""}
                  onChange={handleChange}
                  placeholder="Enter Amount"
                />
              </div>
              {/* <div className="col-lg">
                <label>Payment Method <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  name="payment_method"
                  value={inputs.payment_method || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Payment Method</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="online">Online Payment</option>
                </select>
              </div> */}
            {/* </div>

            <div className="row mt-3"> */}
              <div className="col-lg">
                <label>Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={inputs.description || ""}
                  onChange={handleChange}
                  placeholder="Enter Description"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-3">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-save mg-r-10"></i> Update
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentEdit;